import { useState, useEffect } from 'react';
import { Button } from '../ui/Button';
import { Alert } from '../ui/Alert';
import { getIdToken } from '../../config/firebase';

interface User {
  email: string;
  name: string;
  isAdmin: boolean;
  createdAt: any;
}

interface PendingRequest {
  id: string;
  email: string;
  name: string;
  requestedAt: any;
  status: 'pending' | 'approved' | 'rejected';
}

interface AdminPanelProps {
  onClose: () => void;
}

export function AdminPanel({ onClose }: AdminPanelProps) {
  const [activeTab, setActiveTab] = useState<'users' | 'requests'>('requests');
  const [users, setUsers] = useState<User[]>([]);
  const [pendingRequests, setPendingRequests] = useState<PendingRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newUserIsAdmin, setNewUserIsAdmin] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const getAuthHeaders = async () => {
    const token = await getIdToken();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();

      const [usersRes, requestsRes] = await Promise.all([
        fetch('/api/firebase-auth/whitelist', { headers }),
        fetch('/api/firebase-auth/pending-requests', { headers })
      ]);

      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setUsers(usersData.users || []);
      }

      if (requestsRes.ok) {
        const requestsData = await requestsRes.json();
        setPendingRequests(requestsData.requests || []);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (request: PendingRequest) => {
    setActionLoading(request.id);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`/api/firebase-auth/approve-request/${request.id}`, {
        method: 'POST',
        headers
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error);
      }

      await loadData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (request: PendingRequest) => {
    setActionLoading(request.id);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`/api/firebase-auth/reject-request/${request.id}`, {
        method: 'POST',
        headers
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error);
      }

      await loadData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemoveUser = async (email: string) => {
    if (!confirm(`¿Estás seguro de eliminar a ${email}?`)) return;

    setActionLoading(email);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`/api/firebase-auth/whitelist/${encodeURIComponent(email)}`, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error);
      }

      await loadData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleAdmin = async (email: string, currentIsAdmin: boolean) => {
    setActionLoading(email);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`/api/firebase-auth/whitelist/${encodeURIComponent(email)}/toggle-admin`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ isAdmin: !currentIsAdmin })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error);
      }

      await loadData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUserEmail.trim()) return;

    setActionLoading('add-user');
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch('/api/firebase-auth/whitelist', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          email: newUserEmail.trim().toLowerCase(),
          name: newUserName.trim() || newUserEmail.split('@')[0],
          isAdmin: newUserIsAdmin
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error);
      }

      setNewUserEmail('');
      setNewUserName('');
      setNewUserIsAdmin(false);
      setShowAddUser(false);
      await loadData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp._seconds
      ? new Date(timestamp._seconds * 1000)
      : new Date(timestamp);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6 text-white">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">Panel de Administración</h2>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-4 mt-4">
            <button
              onClick={() => setActiveTab('requests')}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                activeTab === 'requests'
                  ? 'bg-white text-purple-600'
                  : 'bg-white/20 hover:bg-white/30'
              }`}
            >
              Solicitudes pendientes
              {pendingRequests.length > 0 && (
                <span className="ml-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {pendingRequests.length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('users')}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                activeTab === 'users'
                  ? 'bg-white text-purple-600'
                  : 'bg-white/20 hover:bg-white/30'
              }`}
            >
              Usuarios ({users.length})
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {error && <div className="mb-4"><Alert type="error">{error}</Alert></div>}

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            </div>
          ) : activeTab === 'requests' ? (
            /* Pending Requests */
            pendingRequests.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p>No hay solicitudes pendientes</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingRequests.map((request) => (
                  <div
                    key={request.id}
                    className="border rounded-xl p-4 flex items-center justify-between hover:shadow-md transition"
                  >
                    <div>
                      <p className="font-semibold text-gray-900">{request.name}</p>
                      <p className="text-gray-600">{request.email}</p>
                      <p className="text-sm text-gray-400">Solicitado: {formatDate(request.requestedAt)}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleApprove(request)}
                        disabled={actionLoading === request.id}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2"
                      >
                        {actionLoading === request.id ? '...' : 'Aprobar'}
                      </Button>
                      <Button
                        onClick={() => handleReject(request)}
                        disabled={actionLoading === request.id}
                        className="bg-red-600 hover:bg-red-700 text-white px-4 py-2"
                      >
                        {actionLoading === request.id ? '...' : 'Rechazar'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            /* Users List */
            <div className="space-y-4">
              {/* Add User Button/Form */}
              {!showAddUser ? (
                <button
                  onClick={() => setShowAddUser(true)}
                  className="w-full border-2 border-dashed border-gray-300 rounded-xl p-4 text-gray-500 hover:border-purple-400 hover:text-purple-600 transition flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Añadir usuario manualmente
                </button>
              ) : (
                <form onSubmit={handleAddUser} className="border-2 border-purple-200 rounded-xl p-4 bg-purple-50">
                  <h3 className="font-semibold text-purple-900 mb-3">Añadir nuevo usuario</h3>
                  <div className="space-y-3">
                    <input
                      type="email"
                      value={newUserEmail}
                      onChange={(e) => setNewUserEmail(e.target.value)}
                      placeholder="Email del usuario *"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <input
                      type="text"
                      value={newUserName}
                      onChange={(e) => setNewUserName(e.target.value)}
                      placeholder="Nombre (opcional)"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    <label className="flex items-center gap-2 text-gray-700">
                      <input
                        type="checkbox"
                        checked={newUserIsAdmin}
                        onChange={(e) => setNewUserIsAdmin(e.target.checked)}
                        className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                      />
                      Dar permisos de administrador
                    </label>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button
                      type="submit"
                      disabled={actionLoading === 'add-user' || !newUserEmail.trim()}
                      className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2"
                    >
                      {actionLoading === 'add-user' ? 'Añadiendo...' : 'Añadir usuario'}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => {
                        setShowAddUser(false);
                        setNewUserEmail('');
                        setNewUserName('');
                        setNewUserIsAdmin(false);
                      }}
                      className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2"
                    >
                      Cancelar
                    </Button>
                  </div>
                </form>
              )}

              {/* Users list */}
              {users.map((user) => (
                <div
                  key={user.email}
                  className="border rounded-xl p-4 flex items-center justify-between hover:shadow-md transition"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                      user.isAdmin ? 'bg-purple-600' : 'bg-gray-400'
                    }`}>
                      {user.name?.charAt(0).toUpperCase() || user.email.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 flex items-center gap-2">
                        {user.name || user.email.split('@')[0]}
                        {user.isAdmin && (
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                            Admin
                          </span>
                        )}
                      </p>
                      <p className="text-gray-600 text-sm">{user.email}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleToggleAdmin(user.email, user.isAdmin)}
                      disabled={actionLoading === user.email}
                      className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 text-sm"
                    >
                      {user.isAdmin ? 'Quitar admin' : 'Hacer admin'}
                    </Button>
                    <Button
                      onClick={() => handleRemoveUser(user.email)}
                      disabled={actionLoading === user.email}
                      className="bg-red-100 hover:bg-red-200 text-red-700 px-3 py-2 text-sm"
                    >
                      Eliminar
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t p-4 flex justify-end">
          <Button onClick={loadData} className="bg-gray-100 hover:bg-gray-200 text-gray-700">
            Actualizar
          </Button>
        </div>
      </div>
    </div>
  );
}
